import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import SEOHead, { SITE_URL, DEFAULT_OG_IMAGE } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { EditionWithItems } from "@/types/newsletter";
import Navbar from "@/components/Navbar";
import { format } from "date-fns";
import { formatGenerationHourLocal } from "@/lib/formatDeliveryTime";
import { ArrowRight, ChevronDown, Send, MessageSquare, Users2 } from "lucide-react";
import { useInView } from "@/hooks/useInView";
import SignupModal from "@/components/SignupModal";
import CookieConsent from "@/components/CookieConsent";
import CopyableCode from "@/components/CopyableCode";
import logoFull from "@/assets/logo.png";
import logoIcon from "@/assets/logo-icon.png";
import SerifReveal from "@/components/motion/SerifReveal";
import RevealOnScroll from "@/components/motion/RevealOnScroll";
import MagneticButton from "@/components/motion/MagneticButton";
import ScrollProgressBar from "@/components/decor/ScrollProgressBar";
import GridPaper from "@/components/decor/GridPaper";
import { motion } from "motion/react";

const FeaturesShowcase = lazy(() => import("@/components/FeaturesShowcase"));
const EditionView = lazy(() => import("@/components/EditionView"));
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const COMPANIES = [
  { name: "Salla", url: "https://salla.com" },
  { name: "Foodics", url: "https://foodics.com" },
  { name: "Tamara", url: "https://tamara.co" },
  { name: "Jasper", url: "https://jasper.ai" },
  { name: "Copy.ai", url: "https://copy.ai" },
  { name: "Writer", url: "https://writer.com" },
  { name: "Synthesia", url: "https://synthesia.io" },
  { name: "Runway", url: "https://runwayml.com" },
  { name: "Tabby", url: "https://tabby.ai" },
  { name: "Lean Technologies", url: "https://leantech.me" },
  { name: "Hala", url: "https://hala.com" },
  { name: "Cohere", url: "https://cohere.com" },
];

const TESTIMONIALS = [
  { q: "thanks for tracking it all. what used to be a month's headlines now happen daily", stars: 5, name: "R.A.", role: "VP of Engineering" },
  { q: "the world is moving faster than the algorithms can refresh. if you're waiting for the evening news, you're already obsolete", stars: 5, name: "M.T.", role: "Product Lead" },
  { q: "for someone who loves AI, you give me an awful lot of reasons not to love it", stars: 5, name: "L.K.", role: "Senior Frontend Developer" },
  { q: "my favorite part of the day", stars: 4, name: "D.S.", role: "UX Writer" },
  { q: "replaced my entire morning doomscroll tbh", stars: 5, name: "S.K.", role: "Engineering Manager" },
  { q: "my coworkers think i follow AI news closely. i just read this", stars: 4, name: "J.M.", role: "Marketing Director" },
];


const Index = () => {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const refId = searchParams.get("ref");
  const [editions, setEditions] = useState<EditionWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroEmail, setHeroEmail] = useState("");
  const [modalEdition, setModalEdition] = useState<EditionWithItems | null>(null);
  const [showSticky, setShowSticky] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const heroRef = useRef<HTMLElement>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);
  

  const howSection = useInView();
  const feedbackSection = useInView();

  // Auto-focus hero email on desktop after animation
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    if (mq.matches) {
      const t = setTimeout(() => heroInputRef.current?.focus(), 800);
      return () => clearTimeout(t);
    }
  }, []);

  // Sticky mobile CTA
  useEffect(() => {
    const onScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setShowSticky(heroBottom < -50);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);


  useEffect(() => {
    const fetchEditions = async () => {
      const { data } = await supabase
        .from("editions")
        .select("id, slug, edition_date, hook, kicker, custom_message, writers_take, stat_number, stat_caption, quote_text, quote_attribution, one_more_thing, one_more_thing_source, status, generation_hour, edition_items(id, body, source_name, source_url, item_order)")
        .in("status", ["approved", "sent"])
        .eq("generation_hour", 7)
        .order("edition_date", { ascending: false })
        .limit(5);

      if (data) {
        setEditions(data as EditionWithItems[]);
      }
      setLoading(false);
    };
    fetchEditions();
  }, []);

  const latestEdition = editions[0] ?? null;

  const handleOneStepSubscribe = useCallback((email: string) => {
    if (!email) return;
    setSignupOpen(true);
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-accent animate-pulse-live" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/editions" replace />;
  }

  return (
    <main className="min-h-screen bg-background">
      <SEOHead
        title="aibly: The #1 Free Daily AI Newsletter"
        description="Stay ahead of AI in just 2 minutes a day. The #1 free daily newsletter delivering the most important artificial intelligence news."
        canonical="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "aibly",
            url: SITE_URL,
            description: "Free daily AI newsletter delivering the most important artificial intelligence news in 2 minutes.",
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "aibly",
            url: SITE_URL,
            logo: DEFAULT_OG_IMAGE,
            sameAs: [],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "What do I get when I subscribe?", acceptedAnswer: { "@type": "Answer", text: "A daily 2-minute briefing with the most important AI news, delivered to your inbox at your chosen time. Every edition includes sourced stories, a stat of the day, a notable quote, a sharp hot take you can vote on, and on Fridays, a weekly quiz." } },
              { "@type": "Question", name: "How often does aibly publish?", acceptedAnswer: { "@type": "Answer", text: "Every single day. You pick your delivery time and we show up on schedule, weekends included. Fridays are special: you get a quiz built from the week's stories." } },
              { "@type": "Question", name: "Is it really free?", acceptedAnswer: { "@type": "Answer", text: "Yes. The core daily briefing is free and always will be. No credit card required. Just sign up with your email." } },
              { "@type": "Question", name: "What are XP and levels?", acceptedAnswer: { "@type": "Answer", text: "Every time you read an edition, vote on a hot take, bookmark a story, or ace a quiz question, you earn XP. Your XP unlocks levels, from Lurker all the way to Prophet." } },
              { "@type": "Question", name: "Can I pick what AI topics I get?", acceptedAnswer: { "@type": "Answer", text: "Yes. During signup pick 3–7 from 20 AI lanes: Foundation Models, Agents, Robotics, Open Source, China AI, Hardware, Regulation and more. Matching stories float to the top of every edition. Prefer the firehose? Tap 'Show me everything' for the popular mix. You can edit your picks once every 24 hours." } },
              { "@type": "Question", name: "What topics do you cover?", acceptedAnswer: { "@type": "Answer", text: "The full AI landscape: large language models, computer vision, robotics, regulation, startup funding, enterprise adoption, open-source releases, and more." } },
              { "@type": "Question", name: "How is aibly different from just reading the news?", acceptedAnswer: { "@type": "Answer", text: "We scan hundreds of sources so you don't have to. You get a distilled, opinionated briefing, not a link dump, plus interactive features like voting, quizzes, and XP tracking." } },
            ],
          },
        ]}
      >
        <link rel="alternate" type="application/rss+xml" title="aibly RSS Feed" href={`${SITE_URL}/rss.xml`} />
      </SEOHead>
      <Navbar onSubscribeClick={() => setSignupOpen(true)} />
      <ScrollProgressBar />

      {/* ─── HERO ─── */}
      <section ref={heroRef} className="pt-28 pb-10 px-4 relative">
        <div className="max-w-3xl mx-auto text-center space-y-7">
          <SerifReveal
            as="h1"
            text="Free Daily AI Newsletter"
            className="font-display text-5xl md:text-7xl italic leading-[1.05] text-foreground tracking-tight"
          />

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="text-muted-foreground text-sm md:text-base max-w-lg mx-auto leading-relaxed"
          >
            While you were offline, AI reshaped the world. We caught every move, so you don't have to.
            2 minutes. Every day. Tuned to the AI topics you actually care about.
          </motion.p>

          <motion.form
            onSubmit={(e) => { e.preventDefault(); handleOneStepSubscribe(heroEmail); }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
          >
            <Input
              ref={heroInputRef}
              aria-label="Email address for free subscription"
              type="email"
              placeholder="your@email.com"
              value={heroEmail}
              onChange={(e) => setHeroEmail(e.target.value)}
              required
              className="rounded-none bg-secondary border-border text-foreground placeholder:text-muted-foreground text-sm flex-1"
            />
            <MagneticButton
              type="submit"
              className="btn-shine rounded-none text-xs tracking-widest uppercase px-6 py-3 gap-2 inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              ariaLabel="Subscribe free"
            >
              Subscribe free <ArrowRight className="w-3 h-3 ml-1.5" />
            </MagneticButton>
          </motion.form>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="text-[10px] text-muted-foreground tracking-wider uppercase"
          >
            Free forever · No spam · Unsubscribe anytime · <span className="text-accent">Takes 10 sec ⚡</span>
          </motion.p>
        </div>

      </section>

      {/* ─── SOCIAL PROOF MARQUEE ─── */}
      <section className="py-8 border-t border-border overflow-hidden">
        <p className="text-[10px] tracking-widest uppercase text-muted-foreground text-center mb-6">
          Read by professionals at
        </p>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-background to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-background to-transparent z-10" />
          <div className="flex animate-marquee w-max">
            {[...Array(2)].map((_, setIdx) => (
              <div key={setIdx} className="flex items-center gap-10 sm:gap-14 px-5">
                {COMPANIES.map((company) => (
                  <a
                    key={`${setIdx}-${company.name}`}
                    href={company.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm sm:text-base tracking-widest uppercase text-muted-foreground hover:text-foreground font-medium whitespace-nowrap transition-colors"
                  >
                    {company.name}
                  </a>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LATEST EDITION PREVIEW (grid-paper frame) ─── */}
      {latestEdition && (
        <section className="py-16 px-4 border-t border-border">
          <div className="max-w-2xl mx-auto space-y-6">
            <RevealOnScroll>
              <div className="text-center space-y-2">
                <p className="text-[10px] tracking-[0.4em] uppercase text-accent font-mono">
                  Today's edition
                </p>
                <p className="text-[10px] text-muted-foreground tracking-widest uppercase font-mono">
                  {format(new Date(latestEdition.edition_date + "T00:00:00"), "EEEE, MMMM d, yyyy")}
                  {latestEdition.generation_hour != null && (
                    <span> · {formatGenerationHourLocal(latestEdition.generation_hour)}</span>
                  )}
                </p>
              </div>
            </RevealOnScroll>

            <RevealOnScroll y={20} delay={0.1}>
              <GridPaper className="p-4 sm:p-8" spacing={20} opacity={0.5}>
                <div className="bg-background border border-border">
                  <div className="p-6 sm:p-8 space-y-5">
                    {latestEdition.hook && (
                      <p className="font-display text-foreground italic text-xl md:text-2xl leading-snug">
                        {latestEdition.hook}
                      </p>
                    )}

                    {latestEdition.stat_number && (
                      <div className="flex items-start gap-4 pt-3 border-t border-border">
                        <span className="text-accent text-3xl md:text-4xl font-display font-bold shrink-0 leading-none">
                          {latestEdition.stat_number}
                        </span>
                        <p className="text-xs text-muted-foreground leading-relaxed pt-2">
                          {latestEdition.stat_caption}
                        </p>
                      </div>
                    )}

                    <div className="space-y-5 pt-2">
                      {latestEdition.edition_items
                        .sort((a, b) => a.item_order - b.item_order)
                        .slice(0, 2)
                        .map((item, i) => (
                          <div key={item.id} className="space-y-2">
                            <p className="text-[10px] text-accent tracking-[0.3em] uppercase font-mono">
                              {String(i + 1).padStart(2, "0")}
                            </p>
                            <p className="text-foreground/90 text-sm leading-relaxed line-clamp-3">
                              {item.body}
                            </p>
                            {item.source_name && (
                              <span className="text-[10px] text-muted-foreground tracking-wider uppercase">
                                [{item.source_name}]
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="content-gate-gradient" />

                  <div className="relative bg-background border-t border-border px-6 py-4 flex items-center justify-between gap-3">
                    <p className="text-xs text-muted-foreground">
                      +{Math.max(0, latestEdition.edition_items.length - 2)} more in this edition
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-none text-xs tracking-widest uppercase shrink-0"
                      onClick={() => setModalEdition(latestEdition)}
                    >
                      Read full edition →
                    </Button>
                  </div>
                </div>
              </GridPaper>
            </RevealOnScroll>
          </div>
        </section>
      )}

      {/* ─── HOW IT WORKS (editorial timeline) ─── */}
      <section id="how-it-works" ref={howSection.ref} className="py-20 px-4 border-t border-border">
        <div className="max-w-2xl mx-auto">
          <RevealOnScroll>
            <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground font-mono mb-12 text-center">
              How it works
            </p>
          </RevealOnScroll>

          <ol className="relative">
            {[
              { step: "01", title: "Email + your topics", desc: "Pick 3 to 7 of 20 AI lanes, or tap 'Show me everything'. 30 seconds, max." },
              { step: "02", title: "Read on your schedule", desc: "2 minutes of sharp AI intel. Your topics float to the top, every day." },
              { step: "03", title: "Stay ahead", desc: "You now know more than 99% of people. Act on it." },
            ].map((s, i) => (
              <RevealOnScroll as="li" key={s.step} delay={i * 0.08} className="relative pl-12 sm:pl-20 py-6 border-b border-border last:border-b-0">
                <span className="absolute left-0 top-7 text-accent text-xs font-mono tracking-[0.3em]">
                  {s.step}
                </span>
                <h3 className="font-display italic text-foreground text-2xl sm:text-3xl leading-tight mb-2">
                  {s.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-md">
                  {s.desc}
                </p>
              </RevealOnScroll>
            ))}
          </ol>
        </div>
      </section>

      {/* ─── ONE-LINE PAIN/FIX ─── */}
      <section className="py-20 px-4 border-t border-border">
        <RevealOnScroll>
          <div className="max-w-2xl mx-auto text-center space-y-4">
            <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground font-mono">
              Why email, not a website
            </p>
            <p className="font-display italic text-foreground text-3xl sm:text-5xl leading-[1.1]">
              Bookmarks die.<br />
              <span className="text-accent">Inboxes don't.</span>
            </p>
            <p className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed pt-2">
              You won't come back to a tab. You will open your inbox. We meet you where you already are, on your schedule.
            </p>
          </div>
        </RevealOnScroll>
      </section>

      {/* ─── FEATURES SHOWCASE ─── */}
      <div id="features" className="border-t border-border">
        <Suspense fallback={<div className="py-12" />}>
          <FeaturesShowcase />
        </Suspense>
      </div>

      {/* ─── INTEGRATIONS ─── */}
      <section className="py-20 px-4 border-t border-border">
        <div className="max-w-2xl mx-auto">
          <RevealOnScroll>
            <div className="text-center space-y-3 mb-12">
              <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground font-mono">
                Not just your inbox
              </p>
              <p className="font-display italic text-foreground text-3xl sm:text-5xl leading-[1.1]">
                Read it where<br /><span className="text-accent">you already are.</span>
              </p>
              <p className="text-muted-foreground text-sm max-w-md mx-auto leading-relaxed pt-2">
                Email is the default. It isn't the only way in — aibly ships wherever your day already happens, from a personal Telegram feed to your team's Slack or Teams channel.
              </p>
            </div>
          </RevealOnScroll>

          <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border border-t border-b border-border">
            {[
              {
                icon: Send,
                title: "Telegram",
                desc: "Join the public channel free, no account needed. Already an aibly subscriber? Link your account from Profile for a personal briefing filtered to your topics.",
                cta: "Join @aibly_newsletter →",
                href: "https://t.me/aibly_newsletter",
              },
              {
                icon: MessageSquare,
                title: "Slack",
                desc: (
                  <>
                    In any channel, run{" "}
                    <CopyableCode text="/feed subscribe https://aibly.club/rss.xml" className="text-[10px] bg-secondary px-1 py-0.5 border border-border" />
                    {" "}— Slack's built-in RSS posts new editions automatically.
                  </>
                ),
              },
              {
                icon: Users2,
                title: "Microsoft Teams",
                desc: (
                  <>
                    In a channel: <strong>+</strong> → <strong>Workflows</strong> → "Post to a channel when a new feed item is published" → paste{" "}
                    <CopyableCode text="https://aibly.club/rss.xml" className="text-[10px] bg-secondary px-1 py-0.5 border border-border" />.
                  </>
                ),
              },
            ].map((tool, i) => (
              <RevealOnScroll key={tool.title} delay={i * 0.08} className="p-6 space-y-3">
                <tool.icon className="w-4 h-4 text-accent" />
                <h3 className="font-display italic text-foreground text-lg">{tool.title}</h3>
                <p className="text-muted-foreground text-xs leading-relaxed">{tool.desc}</p>
                {tool.cta && (
                  <a href={tool.href} target="_blank" rel="noopener noreferrer" className="text-[11px] text-accent hover:underline tracking-wide inline-block pt-1">
                    {tool.cta}
                  </a>
                )}
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ (hairline only) ─── */}
      <section id="faq" className="py-20 px-4 border-t border-border">
        <div className="max-w-2xl mx-auto">
          <RevealOnScroll>
            <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground font-mono mb-12 text-center">
              Questions, answered
            </p>
          </RevealOnScroll>
          <div>
            {[
              { q: "What do I get when I subscribe?", a: "A daily 2-minute briefing with the most important AI news, delivered to your inbox at your chosen time. Every edition includes sourced stories, a stat of the day, a notable quote, a sharp hot take you can vote on (agree or disagree), and on Fridays, a weekly quiz to test how closely you followed the week's AI news." },
              { q: "How often does aibly publish?", a: "Every single day. You pick your delivery time and we show up on schedule, weekends included. Fridays are special: you get a quiz built from the week's stories." },
              { q: "Is it really free?", a: "Yes. The core daily briefing is free and always will be. No credit card required. Just sign up with your email." },
              { q: "What are XP and levels?", a: "Every time you read an edition, vote on a hot take, bookmark a story, ace a quiz question, or get a friend to sign up through your referral link, you earn XP. Your XP unlocks levels, from Lurker all the way to Prophet. It's our way of rewarding the most engaged readers." },
              { q: "Can I pick what AI topics I get?", a: "Yes. During signup pick 3 to 7 from 20 AI lanes: Foundation Models, Agents, Robotics, Open Source, China AI, Hardware, Regulation and more. Matching stories float to the top of every edition. Prefer the firehose? Tap 'Show me everything' for the popular mix. You can edit your picks once every 24 hours from your profile." },
              { q: "What topics do you cover?", a: "The full AI landscape: large language models, computer vision, robotics, regulation, startup funding, enterprise adoption, open-source releases, and more." },
              { q: "Can I get aibly outside of email?", a: "Yes. Join the public Telegram channel (@aibly_newsletter) for the daily digest with no account needed, or link your aibly account from Profile to get your personal, topic-filtered briefing sent to Telegram instead — same content, same schedule, just a different inbox. Teams already on Slack or Microsoft Teams can pipe every edition straight into a channel via our RSS feed, no bot install required." },
              { q: "How is aibly different from just reading the news?", a: "We scan hundreds of sources so you don't have to. You get a distilled, opinionated briefing, not a link dump, plus interactive features like voting, quizzes, and XP tracking. Two minutes, then you move on with your day." },
            ].map((faq, i) => (
              <details key={i} className="group border-b border-border" open={i === 0}>
                <summary className="py-5 cursor-pointer text-base text-foreground hover:text-accent transition-colors list-none flex items-center justify-between gap-4">
                  <span className="font-display italic">{faq.q}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground group-open:rotate-180 transition-transform shrink-0" />
                </summary>
                <div className="pb-5 pr-8">
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CLOSING (CTA + Fuel the mission, merged) ─── */}
      <section className="py-24 px-4 border-t border-border">
        <RevealOnScroll>
          <div className="max-w-xl mx-auto text-center space-y-6">
            <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground font-mono">
              One last thing
            </p>
            <p className="font-display italic text-foreground text-3xl sm:text-5xl leading-[1.1]">
              AI never sleeps.<br />Neither does aibly.
            </p>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto">
              Free, daily, delivered when you want it. Built by humans, for humans keeping up with AI.
            </p>

            <div className="pt-4">
              <MagneticButton
                onClick={() => setSignupOpen(true)}
                className="btn-shine rounded-none text-xs tracking-widest uppercase px-10 py-4 gap-2 inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                ariaLabel="Never miss a day"
              >
                Never miss a day <ArrowRight className="w-3 h-3 ml-1.5" />
              </MagneticButton>
            </div>

            <p className="text-[11px] text-muted-foreground pt-6">
              Already a reader and want to support the mission?{" "}
              <a
                href="https://jami.bio/nabiloo19/tip"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline tracking-wider"
              >
                ☕ Fuel the Signal
              </a>
            </p>
          </div>
        </RevealOnScroll>
      </section>


      {/* ─── FOOTER ─── */}
      <footer className="border-t border-border">
        <div className="max-w-2xl mx-auto px-4 py-12 space-y-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <img src={logoIcon} alt="aibly logo" width={500} height={500} className="h-16 w-auto dark:invert sm:hidden" />
                <img src={logoFull} alt="aibly logo" width={486} height={135} className="h-8 w-auto dark:invert hidden sm:block" />
              </div>
              <p className="text-xs text-muted-foreground max-w-xs leading-relaxed">
                AI news distilled into 2 minutes of sharp intel. On your schedule.
              </p>
            </div>

            <div className="flex gap-8">
              <div className="space-y-2">
                <p className="text-[10px] tracking-widest uppercase text-muted-foreground">Explore</p>
                <div className="flex flex-col gap-1">
                  <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2">How It Works</a>
                  <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2">Features</a>
                  <a href="#faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2">FAQ</a>
                  <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2">Login</Link>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] tracking-widest uppercase text-muted-foreground">Company</p>
                <div className="flex flex-col gap-1">
                  <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2">About</Link>
                  <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2">Privacy</Link>
                  <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors py-2">Terms</Link>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[10px] text-muted-foreground tracking-wider">
              © {new Date().getFullYear()} aibly. All rights reserved.
            </p>
            <div className="flex items-center gap-3">
              <a href="mailto:hello@aibly.club" className="text-[10px] text-muted-foreground hover:text-foreground transition-colors tracking-wider">
                hello@aibly.club
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* ─── STICKY MOBILE CTA BAR ─── */}
      {showSticky && !signupOpen && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3 animate-slide-up sm:hidden">
          <Button
            onClick={() => setSignupOpen(true)}
            className="btn-shine rounded-none text-xs tracking-widest uppercase w-full py-4 gap-2"
          >
            Subscribe in 10 sec <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      )}

      <SignupModal open={signupOpen} onOpenChange={setSignupOpen} defaultEmail={heroEmail} refId={refId} />

      {/* ─── EDITION MODAL (with subscribe CTA for logged-out) ─── */}
      <Dialog open={!!modalEdition} onOpenChange={(open) => !open && setModalEdition(null)}>
        <DialogContent className="max-w-3xl p-0 gap-0 max-h-[90vh] overflow-hidden">
          <ScrollArea className="max-h-[90vh] pt-12 px-6 pb-6">
            <Suspense fallback={<div className="py-12 text-center text-muted-foreground text-xs">Loading…</div>}>
              {modalEdition && <EditionView edition={modalEdition} />}
            </Suspense>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <CookieConsent />
    </main>
  );
};

export default Index;
